/**
 * InvoiceFilterDrawer Component
 * Mobile filter sheet for invoices
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { InvoiceFilters } from '../types/invoice.types';
import type { InvoiceStatus } from '@/types/invoice';

// ============================================
// STATUS OPTIONS
// ============================================

const STATUS_OPTIONS: { value: InvoiceStatus; label: string; description: string }[] = [
  { value: 'DRAFT', label: 'Draft', description: 'Invoices not yet issued' },
  { value: 'ISSUED', label: 'Issued', description: 'Invoices sent to customers' },
  { value: 'PAID', label: 'Paid', description: 'Fully paid invoices' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid', description: 'Partial payments received' },
  { value: 'OVERDUE', label: 'Overdue', description: 'Payment overdue invoices' },
  { value: 'CANCELLED', label: 'Cancelled', description: 'Cancelled invoices' },
];

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoiceFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: InvoiceFilters;
  onStatusToggle: (status: InvoiceStatus) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onClearFilters: () => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoiceFilterDrawer Component
 * Bottom sheet for mobile filter controls
 *
 * @example
 * ```tsx
 * <InvoiceFilterDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   filters={filters}
 *   onStatusToggle={toggleStatus}
 *   onDateRangeChange={setDateRange}
 *   onClearFilters={clearFilters}
 * />
 * ```
 */
export function InvoiceFilterDrawer({
  open,
  onOpenChange,
  filters,
  onStatusToggle,
  onDateRangeChange,
  onClearFilters,
}: InvoiceFilterDrawerProps) {
  const [dateFromInput, setDateFromInput] = useState(filters.dateFrom || '');
  const [dateToInput, setDateToInput] = useState(filters.dateTo || '');

  const selectedStatuses = filters.statuses || [];

  const handleClearAllFilters = () => {
    setDateFromInput('');
    setDateToInput('');
    onClearFilters();
  };

  const handleApplyFilters = () => {
    onDateRangeChange(dateFromInput, dateToInput);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh]">
        <SheetHeader>
          <SheetTitle>Filter Invoices</SheetTitle>
          <SheetDescription>
            Select filters to refine your invoice list
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="mt-6 h-[400px] pr-4">
          <div className="space-y-6">
            {/* Status Filter Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">Status</h3>
              <div className="space-y-3">
                {STATUS_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-start space-x-3 cursor-pointer"
                    onClick={() => onStatusToggle(option.value)}
                  >
                    <Checkbox
                      id={option.value}
                      checked={selectedStatuses.includes(option.value)}
                      onCheckedChange={() => onStatusToggle(option.value)}
                    />
                    <div className="flex-1 min-w-0">
                      <Label
                        htmlFor={option.value}
                        className="font-medium text-slate-900 cursor-pointer"
                      >
                        {option.label}
                      </Label>
                      <p className="text-sm text-slate-600 mt-0.5">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range Filter Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">Date Range</h3>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="date-from" className="text-sm text-slate-600">
                    From Date
                  </Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFromInput}
                    onChange={(e) => setDateFromInput(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="date-to" className="text-sm text-slate-600">
                    To Date
                  </Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateToInput}
                    onChange={(e) => setDateToInput(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-6 gap-2 flex-row">
          <Button
            variant="outline"
            onClick={handleClearAllFilters}
            className="flex-1"
          >
            Clear All
          </Button>
          <Button
            onClick={handleApplyFilters}
            className="flex-1"
          >
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default InvoiceFilterDrawer;
